import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PriceButton } from "@/components/AgentPrices";
import { productForm, productApplication } from "@/lib/productInfo";

/**
 * Vetrina del catalogo TOP-TEC 2026 all'interno della landing cliente
 * (post-login). Stesse 8 famiglie della pagina di accesso, con la differenza
 * che il click su una scheda prodotto apre un modale dettaglio con
 * immagine ingrandita, descrizione completa e CTA "Richiedi scheda tecnica".
 */
export default function ClientCatalogShowcase() {
  const [catalog, setCatalog] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [detailProduct, setDetailProduct] = useState(null); // product summary

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/catalog");
        setCatalog(data || []);
        const first = (data || []).find((c) => c.count > 0);
        if (first) setActiveCat(first.slug);
      } catch {
        // vetrina non critica
      }
    })();
  }, []);

  const activeCategory = catalog.find((c) => c.slug === activeCat) || null;

  return (
    <section id="catalog-showcase" className="border-t border-slate-800 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-12 flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-3" data-testid="catalog-section-tag">
              [ 03 / Catalogo TOP-TEC 2026 ]
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-white tracking-tighter" data-testid="catalog-section-title">
              8 famiglie, tutta la gamma.
            </h2>
          </div>
          <p className="text-slate-400 max-w-md text-sm">
            Sfoglia il catalogo per famiglia e apri una scheda per vederne descrizione completa e foto ingrandita.
            Da ogni scheda puoi richiedere la documentazione tecnica.
          </p>
        </div>

        {/* 8 category pills */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-8" data-testid="catalog-showcase-grid">
          {catalog.map((cat) => (
            <button
              key={cat.slug}
              type="button"
              onClick={() => cat.count > 0 && setActiveCat(cat.slug)}
              disabled={cat.count === 0}
              className={`relative text-left p-3 border transition-all group ${
                activeCat === cat.slug ? "border-white" : "border-slate-800 hover:border-slate-600"
              } ${cat.count === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              style={{ backgroundColor: activeCat === cat.slug ? cat.color : "transparent" }}
              data-testid={`showcase-cat-${cat.slug}`}
            >
              <div className="w-full h-1 mb-2" style={{ backgroundColor: cat.color }} />
              <div className={`font-heading text-sm leading-tight tracking-tight ${activeCat === cat.slug ? "text-white" : "text-slate-200"}`}>
                {cat.name}
              </div>
              <div className={`font-mono text-[10px] uppercase tracking-widest mt-1 ${activeCat === cat.slug ? "text-white/80" : "text-slate-500"}`}>
                {cat.count > 0 ? `${cat.count} prodotti` : "Prossimamente"}
              </div>
            </button>
          ))}
        </div>

        {/* Products of active category */}
        {activeCategory && (
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 shrink-0" style={{ backgroundColor: activeCategory.color }} />
              <div>
                <h3 className="font-heading text-xl text-white tracking-tighter">{activeCategory.name}</h3>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {activeCategory.count} prodotti
                </div>
              </div>
            </div>
            {activeCategory.count === 0 ? (
              <div className="tm-card p-6 text-center text-slate-400 text-sm">
                Questa famiglia sarà disponibile a breve nel Product Finder.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {activeCategory.products.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setDetailProduct(p)}
                    className="tm-card tm-card-hover p-3 text-left group"
                    data-testid={`showcase-product-${p.id}`}
                  >
                    <div className="aspect-square bg-slate-800/60 border border-slate-700 mb-3 overflow-hidden flex items-center justify-center">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <div className="text-slate-600 font-mono text-xs">TOP-TEC</div>
                      )}
                    </div>
                    <div className="font-heading text-sm text-white tracking-tight leading-tight line-clamp-2 mb-1">
                      {p.name}
                    </div>
                    <div className="text-slate-400 text-xs leading-snug line-clamp-3">
                      {p.brief_description || "—"}
                    </div>
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-orange-400/80 opacity-0 group-hover:opacity-100 transition-opacity">
                      Apri scheda →
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {detailProduct && (
        <ProductDetailModal
          summary={detailProduct}
          onClose={() => setDetailProduct(null)}
        />
      )}
    </section>
  );
}

function ProductDetailModal({ summary, onClose }) {
  const { user } = useAuth();
  const [currentSummary, setCurrentSummary] = useState(summary);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScheda, setShowScheda] = useState(false);
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(true);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setVariantsLoading(true);
    (async () => {
      try {
        const [{ data: fullData }, { data: vars }] = await Promise.all([
          api.get(`/products/${currentSummary.id}`),
          api.get(`/products/${currentSummary.id}/variants`),
        ]);
        if (!cancelled) {
          setFull(fullData);
          setVariants(Array.isArray(vars) ? vars : []);
        }
      } catch {
        if (!cancelled) {
          setFull({ ...currentSummary, description: currentSummary.brief_description || "" });
          setVariants([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setVariantsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentSummary]);

  const p = full || currentSummary;

  return (
    <div className="tm-modal fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="product-detail-modal">
      <div className="tm-card w-full max-w-4xl my-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[var(--tm-surface)] p-4 sm:p-6 md:p-8 border-b border-slate-800 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="tm-tag inline-block mb-3">Scheda prodotto</div>
            <h2 className="font-heading text-2xl md:text-3xl text-white tracking-tighter leading-tight" data-testid="detail-name">
              {p.name}
            </h2>
            {p.brand && (
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                {p.brand}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-200 text-2xl leading-none shrink-0 w-11 h-11 -mr-2 -mt-2 flex items-center justify-center" aria-label="Chiudi" data-testid="detail-close">
            ✕
          </button>
        </div>

        <div className="p-4 sm:p-6 md:p-8 grid md:grid-cols-2 gap-6 md:gap-8">
          {/* Immagine ingrandita */}
          <div>
            <div className="aspect-square bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center">
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" data-testid="detail-image" />
              ) : (
                <div className="text-slate-600 font-mono text-sm">TOP-TEC</div>
              )}
            </div>
          </div>

          {/* Descrizione estesa */}
          <div className="flex flex-col">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              {p.description || p.brief_description ? "Descrizione" : "Dettagli"}
            </div>
            {loading ? (
              <div className="text-slate-500 text-sm italic">Caricamento scheda…</div>
            ) : (
              <>
                {(p.description || p.brief_description) && (
                  <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-line" data-testid="detail-description">
                    {p.description || p.brief_description}
                  </p>
                )}
                <TechDetails p={p} />
              </>
            )}

            <div className="mt-auto pt-6">
              <button
                type="button"
                onClick={() => setShowScheda(true)}
                className="tm-btn-primary w-full"
                data-testid="detail-request-scheda-btn"
              >
                📄 Richiedi scheda tecnica
              </button>
              <PriceButton key={currentSummary.id} productId={currentSummary.id} productName={p.name} className="w-full mt-2" />
              <p className="text-xs text-slate-500 mt-2 text-center">
                Riceverai la documentazione tecnica completa via email.
              </p>
            </div>
          </div>
        </div>

        {/* Varianti della famiglia */}
        {!variantsLoading && variants.length > 0 && (
          <div className="px-6 md:px-8 pb-8 border-t border-slate-800 pt-6" data-testid="variants-section">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">
              Altre varianti della famiglia · {variants.length}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {variants.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setCurrentSummary({
                    id: v.id,
                    name: v.name,
                    brand: v.brand,
                    image_url: v.image_url,
                    category: v.category,
                    brief_description: v.description,
                  })}
                  className="flex items-center gap-3 p-2 border border-slate-800 hover:border-orange-500 hover:bg-orange-500/5 text-left transition-colors group"
                  data-testid={`variant-${v.id}`}
                >
                  <div className="w-12 h-12 shrink-0 bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center">
                    {v.image_url ? (
                      <img src={v.image_url} alt="" className="w-full h-full object-contain" loading="lazy" />
                    ) : (
                      <span className="text-slate-600 font-mono text-[9px]">TOP-TEC</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-xs text-white tracking-tight leading-tight line-clamp-2 group-hover:text-orange-300">
                      {v.name}
                    </div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">
                      Apri →
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {showScheda && (
        <SchedaRequestInlineModal
          product={{ id: currentSummary.id, name: p.name, category: p.category }}
          defaultEmail={user?.email || ""}
          onClose={() => setShowScheda(false)}
          onSuccess={() => { setShowScheda(false); onClose(); }}
        />
      )}
    </div>
  );
}

function techRows(p) {
  return [
    ["Forma", productForm(p)],
    ["Applicazioni", productApplication(p)],
  ].filter(([, v]) => v);
}

function TechDetails({ p }) {
  const rows = techRows(p);
  if (!rows.length) {
    return <p className="text-slate-500 text-sm italic" data-testid="detail-no-info">Nessuna descrizione disponibile.</p>;
  }
  return (
    <dl className="mt-5 border-t border-slate-800 divide-y divide-slate-800" data-testid="detail-tech">
      {rows.map(([k, v]) => (
        <div key={k} className="py-2.5 grid grid-cols-[8.5rem_1fr] gap-3 text-sm">
          <dt className="font-mono text-[10px] uppercase tracking-widest text-slate-500 pt-0.5">{k}</dt>
          <dd className="text-slate-200">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function SchedaRequestInlineModal({ product, defaultEmail, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: "", email: defaultEmail || "", message: "" });
  const [saving, setSaving] = useState(false);
  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("L'email è obbligatoria");
      return;
    }
    setSaving(true);
    try {
      await api.post("/scheda-requests", {
        product_id: product.id,
        client_email: form.email.trim(),
        client_name: form.name.trim() || null,
        message: form.message.trim() || null,
      });
      toast.success(`Richiesta inviata per "${product.name}". Ti contatteremo via email.`);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tm-modal fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="scheda-inline-modal">
      <div className="tm-card p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="tm-tag inline-block mb-4">Scheda tecnica</div>
        <h2 className="font-heading text-2xl text-white tracking-tighter mb-1">
          Richiedi la scheda tecnica
        </h2>
        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-orange-400 mb-1">
            Prodotto
          </div>
          <div className="font-heading text-base text-white tracking-tight leading-tight">
            {product.name}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tm-label">Nome e cognome</label>
            <input type="text" className="tm-input" value={form.name} onChange={setField("name")} data-testid="scheda-inline-name" />
          </div>
          <div>
            <label className="tm-label">Email *</label>
            <input type="email" required className="tm-input" value={form.email} onChange={setField("email")} data-testid="scheda-inline-email" />
          </div>
          <div>
            <label className="tm-label">Messaggio (facoltativo)</label>
            <textarea rows={3} className="tm-input" value={form.message} onChange={setField("message")} placeholder="Es. quantità richieste, applicazione specifica, tempistiche…" data-testid="scheda-inline-message" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="tm-btn-outline flex-1" data-testid="scheda-inline-cancel">
              Annulla
            </button>
            <button type="submit" disabled={saving} className="tm-btn-primary flex-1 disabled:opacity-50" data-testid="scheda-inline-submit">
              {saving ? "Invio..." : "Invia richiesta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
